// Procedural audio (Web Audio). Bus graph: master ← { sfx, ui, engine, ambience }.
// Every sound is synthesised, so no audio assets or licences are needed.

type Bus = 'sfx' | 'ui' | 'engine' | 'ambience';

const dbToGain = (db: number) => Math.pow(10, db / 20);

export class Audio {
  private ctx: AudioContext | null = null;
  private master!: GainNode;
  private buses = {} as Record<Bus, GainNode>;
  private noise!: AudioBuffer;
  private engineOsc: OscillatorNode[] = [];
  private engineGain!: GainNode;
  private engineFilter!: BiquadFilterNode;
  private boostGain!: GainNode;
  private warpGain!: GainNode;
  private warpFilter!: BiquadFilterNode;
  private muted = false;
  volume = 0.8;

  constructor() {
    try {
      const saved = localStorage.getItem('spaceship.audio');
      if (saved) {
        const o = JSON.parse(saved);
        this.volume = o.volume ?? 0.8;
        this.muted = !!o.muted;
      }
    } catch {
      /* storage unavailable */
    }
  }

  /** Must be called from a user gesture. */
  unlock(): void {
    if (this.ctx) {
      void this.ctx.resume();
      return;
    }
    const ctx = new AudioContext();
    this.ctx = ctx;
    this.master = ctx.createGain();
    const limiter = ctx.createDynamicsCompressor();
    limiter.threshold.value = -6;
    limiter.ratio.value = 12;
    this.master.connect(limiter).connect(ctx.destination);
    const levels: Record<Bus, number> = { sfx: -6, ui: -12, engine: -14, ambience: -20 };
    for (const b of Object.keys(levels) as Bus[]) {
      const g = ctx.createGain();
      g.gain.value = dbToGain(levels[b]);
      g.connect(this.master);
      this.buses[b] = g;
    }
    this.noise = ctx.createBuffer(1, ctx.sampleRate * 2, ctx.sampleRate);
    const d = this.noise.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    this.applyVolume();
    this.startEngine();
    this.startAmbience();
  }

  get isMuted(): boolean {
    return this.muted;
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    this.applyVolume();
    this.save();
    return this.muted;
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    this.applyVolume();
    this.save();
  }

  private save(): void {
    try {
      localStorage.setItem('spaceship.audio', JSON.stringify({ volume: this.volume, muted: this.muted }));
    } catch {
      /* ignore */
    }
  }

  private applyVolume(): void {
    if (!this.ctx) return;
    const target = this.muted ? 0 : this.volume * this.volume; // perceptual-ish curve
    this.master.gain.setTargetAtTime(target, this.ctx.currentTime, 0.05);
  }

  private startEngine(): void {
    const ctx = this.ctx!;
    this.engineGain = ctx.createGain();
    this.engineGain.gain.value = 0;
    this.engineFilter = ctx.createBiquadFilter();
    this.engineFilter.type = 'lowpass';
    this.engineFilter.frequency.value = 300;
    this.engineFilter.Q.value = 2;
    this.engineFilter.connect(this.engineGain).connect(this.buses.engine);
    for (const [type, f, g] of [['sawtooth', 55, 0.5], ['sawtooth', 55.7, 0.5], ['square', 27.5, 0.25]] as const) {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = f;
      const og = ctx.createGain();
      og.gain.value = g;
      o.connect(og).connect(this.engineFilter);
      o.start();
      this.engineOsc.push(o);
    }
    // Boost: filtered noise rush.
    const n = ctx.createBufferSource();
    n.buffer = this.noise;
    n.loop = true;
    const bf = ctx.createBiquadFilter();
    bf.type = 'bandpass';
    bf.frequency.value = 900;
    bf.Q.value = 0.7;
    this.boostGain = ctx.createGain();
    this.boostGain.gain.value = 0;
    n.connect(bf).connect(this.boostGain).connect(this.buses.engine);
    n.start();
    // Warp hum: resonant noise + low drone.
    const wn = ctx.createBufferSource();
    wn.buffer = this.noise;
    wn.loop = true;
    this.warpFilter = ctx.createBiquadFilter();
    this.warpFilter.type = 'bandpass';
    this.warpFilter.frequency.value = 300;
    this.warpFilter.Q.value = 3;
    this.warpGain = ctx.createGain();
    this.warpGain.gain.value = 0;
    wn.connect(this.warpFilter).connect(this.warpGain).connect(this.buses.engine);
    wn.start();
    const wd = ctx.createOscillator();
    wd.type = 'sawtooth';
    wd.frequency.value = 36;
    const wdg = ctx.createGain();
    wdg.gain.value = 0.3;
    wd.connect(wdg).connect(this.warpGain);
    wd.start();
  }

  /** 0 off, 0..1 spool/warp level */
  warpLevel(level: number, spooling: boolean): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    this.warpGain.gain.setTargetAtTime(level * (spooling ? 0.6 : 0.9), t, 0.2);
    this.warpFilter.frequency.setTargetAtTime(200 + level * (spooling ? 1600 : 700), t, 0.3);
  }

  warpSpool(): void {
    if (!this.ctx) return;
    this.tone('sfx', 'sawtooth', 80, 900, 0.35, 2.0);
    this.tone('sfx', 'sine', 160, 1800, 0.2, 2.0);
  }

  warpJump(): void {
    if (!this.ctx) return;
    this.noiseHit('sfx', 900, 0.5, 1.0, 1.4);
    this.tone('sfx', 'sine', 60, 25, 1.0, 1.2);
  }

  warpDrop(): void {
    if (!this.ctx) return;
    this.tone('sfx', 'sawtooth', 700, 60, 0.35, 0.9);
    this.noiseHit('sfx', 500, 0.7, 0.7, 0.8);
  }

  private startAmbience(): void {
    const ctx = this.ctx!;
    const out = ctx.createGain();
    out.gain.value = 0.9;
    out.connect(this.buses.ambience);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 420;
    lp.connect(out);
    for (const f of [41.2, 61.7, 82.4, 123.5]) {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = f * (1 + (Math.random() - 0.5) * 0.004);
      const g = ctx.createGain();
      g.gain.value = 0.18;
      const lfo = ctx.createOscillator();
      lfo.frequency.value = 0.03 + Math.random() * 0.05;
      const lg = ctx.createGain();
      lg.gain.value = 0.12;
      lfo.connect(lg).connect(g.gain);
      lfo.start();
      o.connect(g).connect(lp);
      o.start();
    }
    const n = ctx.createBufferSource();
    n.buffer = this.noise;
    n.loop = true;
    const nf = ctx.createBiquadFilter();
    nf.type = 'lowpass';
    nf.frequency.value = 180;
    const ng = ctx.createGain();
    ng.gain.value = 0.25;
    n.connect(nf).connect(ng).connect(out);
    n.start();
  }

  /** Per-frame engine state. */
  engine(throttle: number, boosting: boolean, active: boolean): void {
    if (!this.ctx) return;
    const t = this.ctx.currentTime;
    const th = active ? Math.abs(throttle) : 0;
    this.engineGain.gain.setTargetAtTime(active ? 0.25 + th * 0.75 : 0, t, 0.15);
    this.engineFilter.frequency.setTargetAtTime(220 + th * 1400 + (boosting ? 800 : 0), t, 0.2);
    const pitch = 1 + th * 0.6 + (boosting ? 0.25 : 0);
    this.engineOsc.forEach((o, i) => o.frequency.setTargetAtTime([55, 55.7, 27.5][i] * pitch, t, 0.25));
    this.boostGain.gain.setTargetAtTime(boosting && active ? 0.5 : 0, t, 0.12);
  }

  private vary(v: number, pct = 0.06): number {
    return v * (1 + (Math.random() * 2 - 1) * pct);
  }

  private env(bus: Bus, peak: number, attack: number, decay: number): GainNode {
    const ctx = this.ctx!;
    const g = ctx.createGain();
    const t = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(peak, t + attack);
    g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
    g.connect(this.buses[bus]);
    return g;
  }

  private tone(bus: Bus, type: OscillatorType, f0: number, f1: number, peak: number, dur: number): void {
    const ctx = this.ctx!;
    const o = ctx.createOscillator();
    o.type = type;
    const t = ctx.currentTime;
    o.frequency.setValueAtTime(f0, t);
    o.frequency.exponentialRampToValueAtTime(Math.max(1, f1), t + dur);
    o.connect(this.env(bus, peak, 0.004, dur));
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  private noiseHit(bus: Bus, freq: number, q: number, peak: number, dur: number, type: BiquadFilterType = 'lowpass'): void {
    const ctx = this.ctx!;
    const n = ctx.createBufferSource();
    n.buffer = this.noise;
    n.playbackRate.value = this.vary(1, 0.1);
    const f = ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = freq;
    f.Q.value = q;
    const t = ctx.currentTime;
    n.connect(f).connect(this.env(bus, peak, 0.005, dur));
    n.start(t, Math.random());
    n.stop(t + dur + 0.1);
  }

  /** gain falls off with distance (metres) */
  private near(distance = 0): number {
    return 1 / (1 + distance / 400);
  }

  laser(distance = 0): void {
    if (!this.ctx) return;
    const k = this.near(distance);
    this.tone('sfx', 'square', this.vary(1400), this.vary(260), 0.18 * k, 0.16);
    this.tone('sfx', 'sine', this.vary(2600), this.vary(900), 0.1 * k, 0.1);
  }

  rockHit(distance = 0): void {
    if (!this.ctx) return;
    this.noiseHit('sfx', this.vary(2400), 1.2, 0.35 * this.near(distance), 0.12, 'bandpass');
  }

  shatter(distance = 0): void {
    if (!this.ctx) return;
    const k = this.near(distance);
    this.noiseHit('sfx', 700, 0.8, 0.9 * k, 0.9);
    this.tone('sfx', 'sine', this.vary(110), 35, 0.8 * k, 0.5);
  }

  shieldHit(): void {
    if (!this.ctx) return;
    this.tone('sfx', 'triangle', this.vary(620), 180, 0.45, 0.35);
    this.tone('sfx', 'sine', this.vary(1240), 400, 0.2, 0.3);
    this.noiseHit('sfx', 3000, 1, 0.3, 0.15, 'highpass');
  }

  explosion(distance = 0): void {
    if (!this.ctx) return;
    const k = this.near(distance);
    this.noiseHit('sfx', 500, 0.6, 1.0 * k, 2.2);
    this.tone('sfx', 'sine', 90, 22, 1.0 * k, 1.6);
    this.noiseHit('sfx', 4000, 0.8, 0.4 * k, 0.4, 'highpass');
  }

  landing(): void {
    if (!this.ctx) return;
    this.tone('sfx', 'sine', 70, 40, 0.8, 0.35);
    this.noiseHit('sfx', 400, 0.7, 0.4, 0.4);
  }

  ui(kind: 'hover' | 'click' | 'confirm' | 'ping' | 'log'): void {
    if (!this.ctx) return;
    if (kind === 'hover') this.tone('ui', 'sine', 1800, 1700, 0.08, 0.05);
    if (kind === 'click') this.tone('ui', 'triangle', 900, 600, 0.3, 0.08);
    if (kind === 'confirm') {
      this.tone('ui', 'triangle', 660, 660, 0.3, 0.12);
      setTimeout(() => this.tone('ui', 'triangle', 990, 990, 0.3, 0.2), 90);
    }
    if (kind === 'ping') this.tone('ui', 'sine', 1320, 1320, 0.25, 0.25);
    if (kind === 'log') {
      [523, 659, 784, 1046].forEach((f, i) => setTimeout(() => this.tone('ui', 'triangle', f, f, 0.25, 0.3), i * 90));
    }
  }
}
